<template>
    <div class="exam-container">
        <div class="d-flex align-center mb-6">
            <v-btn-toggle v-model="examStatus" mandatory class="rounded-lg">
                <v-btn value="upcoming" class="px-6">
                    未測驗
                </v-btn>
                <v-btn value="completed" class="px-6">
                    已測驗
                </v-btn>
            </v-btn-toggle>
        </div>

        <!-- 測驗列表 -->
        <v-simple-table class="exam-table">
            <template v-slot:default>
                <thead>
                    <tr>
                        <th v-if="examStatus === 'completed'" class="text-left">測驗日期</th>
                        <th class="text-left">測驗開始日期</th>
                        <th class="text-left">測驗結束日期</th>

                        <th class="text-left">測驗名稱</th>
                        <th class="text-center">操作</th>
                    </tr>
                </thead>
                <tbody v-if="examStatus === 'upcoming'">
                    <tr v-for="exam in upcomingExamList" :key="exam._id">
                        <td class="text-left">{{ formatDateTime(exam.startTime) }}</td>
                        <td class="text-left">{{ formatDateTime(exam.endTime) }}</td>
                        <td class="text-left">{{ exam.name }}</td>
                        <td class="text-center">
                            <v-btn v-if="examStatus === 'upcoming'" small color="grey darken-2" dark class="rounded-lg"
                                @click="startExam(exam, exam.patient)">
                                開始測驗
                            </v-btn>
                            <v-icon v-else small color="grey darken-2" class="rounded-lg" @click="viewScore(exam._id)">
                                mdi-eye
                            </v-icon>
                        </td>
                    </tr>
                </tbody>
                <tbody v-else>
                    <tr v-for="exam in completedExamList" :key="exam._id">
                        <td class="text-left">{{formatDateTime(exam.addDateTime)}}</td>
                        <td class="text-left">{{ formatDateTime(exam.exam.startTime) }}</td>
                        <td class="text-left">{{ formatDateTime(exam.exam.endTime) }}</td>
                        <td class="text-left">{{ exam.exam.name }}</td>
                        <td class="text-center">
                            <v-btn small color="grey darken-2" dark class="rounded-lg" @click="viewAnswerProcess(exam)" style="margin-right: 10px;">
                                查看答題過程
                            </v-btn>
                            <v-btn small color="grey darken-2" dark class="rounded-lg" @click="viewExamScore(exam)">
                                查看成績
                            </v-btn>
                        </td>
                    </tr>
                </tbody>
            </template>
        </v-simple-table>

        <!-- 全螢幕測驗對話框 -->
        <v-dialog v-model="showExamDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
            <v-card class="exam-dialog">
                <!-- 頂部進度條 -->
                <div class="progress-container">
                    <v-btn icon @click="previousStep" v-if="currentStep !== 1">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <v-btn @click="cancelExam" color="black" dark v-if="currentStep === 1">
                        取消測驗
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn text large :class="{ 'active': currentStep === 1 }" class="mr-2 progress-btn"
                        @click="currentStep = 1">
                        面診
                    </v-btn>
                    <v-btn v-if="currentExamPatient.firstVisitQuestionExist" text
                        :class="{ 'active': currentStep === 2 }" large class="mr-2 progress-btn"
                        @click="currentStep = 2">
                        初診
                    </v-btn>
                    <v-btn v-if="currentExamPatient.postOpQuestionExist" text :class="{ 'active': currentStep === 3 }"
                        large class="mr-2 progress-btn" @click="currentStep = 3">
                        術後
                    </v-btn>
                    <v-spacer></v-spacer>
                    <template v-if="currentStep === 1">
                        <v-btn
                            v-if="currentExamPatient.firstVisitQuestionExist || currentExamPatient.postOpQuestionExist"
                            icxon @click="nextStep">
                            <v-icon>mdi-arrow-right</v-icon>
                        </v-btn>
                        <v-btn v-else @click="submitAnswer" color="black" dark>完成測驗</v-btn>
                    </template>

                    <template v-else-if="currentStep === 2">
                        <v-btn v-if="currentExamPatient.postOpQuestionExist" icon @click="nextStep">
                            <v-icon>mdi-arrow-right</v-icon>
                        </v-btn>
                        <v-btn v-else @click="submitAnswer" color="black" dark>完成測驗</v-btn>
                    </template>

                    <template v-else>
                        <v-btn @click="submitAnswer" color="black" dark>完成測驗</v-btn>
                    </template>
                </div>



                <!-- 主要內容區域 -->
                <div class="content-container" v-if="currentStep === 1">
                    <!-- 左側區域 -->
                    <div class="patient-section">
                        <!-- 主圖區域 -->
                        <div class="video-section mb-4">
                            <div class="video-placeholder">
                                <v-img :src="returnImageUrl(currentExamPatient.backgroundImage)" width="100%" height="100%" cover></v-img>
                            </div>
                        </div>

                        <!-- 病患基本資料移到圖片下方 -->
                        <div class="patient-info mb-4">
                            <div class="d-flex justify-space-between mb-2">
                                <div class="info-group">
                                    <div class="info-label text-left">姓名</div>
                                    <v-text-field v-model="currentExamPatient.name" outlined readonly hide-details></v-text-field>
                                </div>
                                <div class="info-group">
                                    <div class="info-label text-left">性別</div>
                                    <v-text-field v-model="currentExamPatient.gender" outlined readonly hide-details></v-text-field>
                                </div>
                                <div class="info-group">
                                    <div class="info-label text-left">年齡</div>
                                    <v-text-field v-model="currentExamPatient.age" outlined readonly hide-details></v-text-field>
                                </div>
                            </div>

                            <!-- 情境說明 -->
                            <div class="chief-complaint">
                                <div class="info-label text-left">情境說明</div>
                                <v-textarea style="overflow: hidden;" v-model="currentExamPatient.description" outlined readonly hide-details rows="8"></v-textarea>
                            </div>

                            <!-- 任務說明 -->
                            <div class="chief-complaint">
                                <div class="info-label text-left">任務說明</div>
                                <v-textarea style="overflow: hidden;" v-model="currentExamPatient.taskDescription" outlined readonly hide-details rows="8"></v-textarea>
                            </div>
                        </div>

                       
                    </div>

                    <!-- 右側聊天區域 -->
                    <div class="exam-form-section">
                            <!-- 圖片區域 (放大到原本個人資料的大小) -->
                            <div class="image-section">
                                <div class="d-flex align-center mb-2">
                                    <span class="subtitle-1 font-weight-bold">圖片</span>
                                </div>
                                <div class="image-slider-container">
                                    <!-- 左箭頭按鈕 -->
                                    <v-btn v-if="currentExamPatient.clinicFiles.length > 2" icon class="slider-arrow left" @click="scrollImages('left')">
                                        <v-icon>mdi-chevron-left</v-icon>
                                    </v-btn>
    
                                    <!-- 圖片容器 -->
                                    <div class="image-scroll-container" ref="imageContainer2">
                                        <div class="image-wrapper" v-for="image in currentExamPatient.clinicFiles" :key="image">
                                            <v-img :src="returnClinicFileUrl(image)" height="200" cover class="rounded-lg cursor-pointer" @click="openImageDialog(returnClinicFileUrl(image))"></v-img>
                                        </div>
                                    </div>
    
                                    <!-- 右箭頭按鈕 -->
                                    <v-btn v-if="currentExamPatient.clinicFiles.length > 2" icon class="slider-arrow right" @click="scrollImages('right')">
                                        <v-icon>mdi-chevron-right</v-icon>
                                    </v-btn>
                                </div>
                            </div>
                        <div class="answer-section">
                         
                            <div class="chat-container">
                                <!-- 聊天訊息區 -->
                                <div class="messages-container" ref="messagesContainer">
                                    <div v-for="message in messages" :key="message._id" :class="['message', message.sender_id === currentUserId ? 'message-sent' : 'message-received']">
                                        {{ message.content }}
                                    </div>
                                    <div v-if="isWaiting" class="message message-received typing-indicator">
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </div>
                                </div>
                                <!-- 輸入區域 -->
                                <div class="input-container">
                                    <v-text-field v-model="messageInput" placeholder="請輸入內容..." outlined dense hide-details @keyup.enter="handleSendMessage" @input="saveTesttoLocalStorage">
                                        <template v-slot:append>
                                            <v-btn icon small @click="handleSendMessage">
                                                <v-icon>mdi-send</v-icon>
                                            </v-btn>
                                            <v-btn icon small @click="handleVoiceInput('messageInput')" :class="{ 'recording': isRecording, 'processing': isProcessing }" :disabled="isProcessing">
                                                <v-icon>{{ isRecording ? 'mdi-microphone-off' : 'mdi-microphone' }}</v-icon>
                                            </v-btn>
                                        </template>
                                    </v-text-field>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 主要內容區域 -->
                <div class="content-container" v-if="currentStep === 2">
                    <!-- 左側病患資訊 -->
                    <div class="patient-section">
                        <v-card class="pa-4">
                            <div class="mb-4">
                                <div class="subtitle-1 font-weight-bold mb-2">姓名</div>
                                <v-text-field v-model="currentExamPatient.name" outlined dense readonly></v-text-field>
                            </div>

                            <div class="mb-4">
                                <div class="subtitle-1 font-weight-bold mb-2">性別</div>
                                <v-text-field v-model="currentExamPatient.gender" outlined dense
                                    readonly></v-text-field>
                            </div>

                            <div class="mb-4">
                                <div class="subtitle-1 font-weight-bold mb-2">年齡</div>
                                <v-text-field v-model="currentExamPatient.age" outlined dense readonly></v-text-field>
                            </div>

                            <div class="mb-4">
                                <div class="subtitle-1 font-weight-bold mb-2">情境說明</div>
                                <v-textarea v-model="currentExamPatient.description" outlined readonly auto-grow
                                    rows="4"></v-textarea>
                            </div>
                            <div class="mb-4">
                                <div class="subtitle-1 font-weight-bold mb-2">任務說明</div>
                                <v-textarea v-model="currentExamPatient.taskDescription" outlined readonly auto-grow
                                    rows="4"></v-textarea>
                            </div>

                            <div class="image-section">
                                <div class="d-flex align-center mb-2">
                                    <span class="subtitle-1 font-weight-bold">圖片</span>
                                </div>
                                <div class="image-slider-container">
                                    <!-- 左箭頭按鈕 -->
                                    <v-btn v-if="currentExamPatient.firstVisitFiles.length > 2" icon
                                        class="slider-arrow left" @click="scrollImages('left', $event)">
                                        <v-icon>mdi-chevron-left</v-icon>
                                    </v-btn>

                                    <!-- 圖片容器 -->
                                    <div class="image-scroll-container" :ref="'imageContainer' + currentStep">
                                        <div class="image-wrapper" v-for="image in currentExamPatient.firstVisitFiles"
                                            :key="image">
                                            <v-img :src="returnFirstVisitFileUrl(image)" width="180" height="120" cover
                                                class="rounded-lg cursor-pointer"
                                                @click="openImageDialog(returnFirstVisitFileUrl(image))"></v-img>
                                        </div>
                                    </div>

                                    <!-- 右箭頭按鈕 -->
                                    <v-btn v-if="currentExamPatient.firstVisitFiles.length > 2" icon
                                        class="slider-arrow right" @click="scrollImages('right', $event)">
                                        <v-icon>mdi-chevron-right</v-icon>
                                    </v-btn>
                                </div>
                            </div>
                        </v-card>
                    </div>

                    <!-- 右側問診表單 -->
                    <div class="exam-form-section">
                        <v-card class="pa-4">
                            <!-- 病患標籤 -->
                            <div class="mb-6">
                                <div class="subtitle-1 font-weight-bold mb-4">病患標籤</div>
                                <div class="d-flex flex-wrap">
                                    <v-chip v-for="tag in currentExamPatient.tags" :key="tag._id" outlined color="black"
                                        class="ma-2 large-chip" text-color="black">
                                        {{ tag.name }}
                                    </v-chip>
                                </div>
                            </div>

                            <!-- 問診列表和回答區域的容器 -->
                            <div class="questions-answer-container">
                                <!-- 左側問題列表 -->
                                <div class="questions-list">
                                    <div v-for="(question, index) in currentExamPatient.firstVisitQuestions"
                                        :key="index" class="nav-item"
                                        :class="{ active: currentFirstVisitQuestionIndex === index }"
                                        @click="currentFirstVisitQuestionIndex = index">
                                        題目{{ index + 1 }}
                                    </div>
                                    <div class="nav-arrows">
                                        <v-btn icon small @click="previousFirstVisitQuestion">
                                            <v-icon>mdi-chevron-left</v-icon>
                                        </v-btn>
                                        <v-btn icon small @click="nextFirstVisitQuestion">
                                            <v-icon>mdi-chevron-right</v-icon>
                                        </v-btn>
                                    </div>
                                </div>

                                <!-- 右側回答區域 -->
                                <div class="answer-container">
                                    <div class="question-title mb-4">問題</div>
                                    <div class="question-text mb-6">{{
                                        currentExamPatient.firstVisitQuestions[currentFirstVisitQuestionIndex].title }}
                                    </div>
                                    <div class="answer-section">
                                        <div class="answer-label mb-2">回答</div>
                                        <v-textarea
                                            v-model="currentExamPatient.firstVisitQuestions[currentFirstVisitQuestionIndex].answer"
                                            outlined placeholder="請輸入回答內容" rows="8" hide-details
                                            @input="saveTesttoLocalStorage"></v-textarea>
                                        <v-btn icon class="ml-2" @click="handleVoiceInput('currentExamPatient.firstVisitQuestions[currentFirstVisitQuestionIndex].answer')">
                                            <v-icon>mdi-microphone</v-icon>
                                        </v-btn>
                                    </div>
                                </div>
                            </div>
                        </v-card>
                    </div>
                </div>

                <!-- 主要內容區域 -->
                <div class="content-container" v-if="currentStep === 3">
                    <!-- 左側病患資訊 -->
                    <div class="patient-section">
                        <v-card class="pa-4">
                            <div class="mb-4">
                                <div class="subtitle-1 font-weight-bold mb-2">姓名</div>
                                <v-text-field v-model="currentExamPatient.name" outlined dense readonly></v-text-field>
                            </div>

                            <div class="mb-4">
                                <div class="subtitle-1 font-weight-bold mb-2">性別</div>
                                <v-text-field v-model="currentExamPatient.gender" outlined dense
                                    readonly></v-text-field>
                            </div>

                            <div class="mb-4">
                                <div class="subtitle-1 font-weight-bold mb-2">年齡</div>
                                <v-text-field v-model="currentExamPatient.age" outlined dense readonly></v-text-field>
                            </div>

                            <div class="mb-4">
                                <div class="subtitle-1 font-weight-bold mb-2">情境說明</div>
                                <v-textarea v-model="currentExamPatient.description" outlined readonly auto-grow
                                    rows="4"></v-textarea>
                            </div>
                            <div class="mb-4">
                                <div class="subtitle-1 font-weight-bold mb-2">任務說明</div>
                                <v-textarea v-model="currentExamPatient.taskDescription" outlined readonly auto-grow
                                    rows="4"></v-textarea>
                            </div>

                            <div class="image-section">
                                <div class="d-flex align-center mb-2">
                                    <span class="subtitle-1 font-weight-bold">圖片</span>
                                </div>
                                <div class="image-slider-container">
                                    <!-- 左箭頭按鈕 -->
                                    <v-btn v-if="currentExamPatient.postOpFiles.length > 2" icon
                                        class="slider-arrow left" @click="scrollImages('left', $event)">
                                        <v-icon>mdi-chevron-left</v-icon>
                                    </v-btn>

                                    <!-- 圖片容器 -->
                                    <div class="image-scroll-container" :ref="'imageContainer' + currentStep">
                                        <div class="image-wrapper" v-for="image in currentExamPatient.postOpFiles"
                                            :key="image">
                                            <v-img :src="returnPostOpFileUrl(image)" width="180" height="120" cover
                                                class="rounded-lg cursor-pointer"
                                                @click="openImageDialog(returnPostOpFileUrl(image))"></v-img>
                                        </div>
                                    </div>

                                    <!-- 右箭頭按鈕 -->
                                    <v-btn v-if="currentExamPatient.postOpFiles.length > 2" icon
                                        class="slider-arrow right" @click="scrollImages('right', $event)">
                                        <v-icon>mdi-chevron-right</v-icon>
                                    </v-btn>
                                </div>
                            </div>
                        </v-card>
                    </div>

                    <!-- 右側問診表單 -->
                    <div class="exam-form-section">
                        <v-card class="pa-4">
                            <!-- 病患標籤 -->
                            <div class="mb-6">
                                <div class="subtitle-1 font-weight-bold mb-4">病患標籤</div>
                                <div class="d-flex flex-wrap">
                                    <v-chip v-for="tag in currentExamPatient.tags" :key="tag._id" outlined color="black"
                                        class="ma-2 large-chip" text-color="black">
                                        {{ tag.name }}
                                    </v-chip>
                                </div>
                            </div>

                            <!-- 問診列表和回答區域的容器 -->
                            <div class="questions-answer-container">
                                <!-- 左側問題列表 -->
                                <div class="questions-list">
                                    <div v-for="(question, index) in currentExamPatient.postOpQuestions" :key="index"
                                        class="nav-item" :class="{ active: currentPostOpQuestionIndex === index }"
                                        @click="currentPostOpQuestionIndex = index">
                                        題目{{ index + 1 }}
                                    </div>
                                    <div class="nav-arrows">
                                        <v-btn icon small @click="previousPostOpQuestion">
                                            <v-icon>mdi-chevron-left</v-icon>
                                        </v-btn>
                                        <v-btn icon small @click="nextPostOpQuestion">
                                            <v-icon>mdi-chevron-right</v-icon>
                                        </v-btn>
                                    </div>
                                </div>

                                <!-- 右側回答區域 -->
                                <div class="answer-container">
                                    <div class="question-title mb-4">問題</div>
                                    <div class="question-text mb-6">{{
                                        currentExamPatient.postOpQuestions[currentPostOpQuestionIndex].title }}</div>
                                    <div class="answer-section">
                                        <div class="answer-label mb-2">回答</div>
                                        <v-textarea
                                            v-model="currentExamPatient.postOpQuestions[currentPostOpQuestionIndex].answer"
                                            outlined placeholder="請輸入回答內容" rows="8" hide-details
                                            @input="saveTesttoLocalStorage"></v-textarea>

                                        <v-btn icon class="ml-2" @click="handleVoiceInput('currentExamPatient.postOpQuestions[currentPostOpQuestionIndex].answer')">
                                            <v-icon>mdi-microphone</v-icon>
                                        </v-btn>
                                    </div>
                                </div>
                            </div>
                        </v-card>
                    </div>
                </div>
            </v-card>
        </v-dialog>

        <v-dialog v-model="showScoreSheet" width="1000">
            <score-sheet :exam-data="examData" @submit="handleSubmit" @export-csv="handleExportCSV"
                @close="handleClose" />
        </v-dialog>

        <!-- 添加圖片放大的 dialog -->
        <v-dialog v-model="showImageDialog">
            <v-card>
                <v-card-actions class="d-flex justify-end pa-2">
                    <v-btn icon @click="showImageDialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-actions>
                <v-img :src="selectedImage" contain height="100%" width="100%"></v-img>
            </v-card>
        </v-dialog>

        <!-- 添加錄音對話框 -->
        <v-dialog v-model="showRecordingDialog" persistent max-width="400">
            <v-card>
                <v-card-title class="headline">
                    語音輸入中
                </v-card-title>
                <v-card-text class="pt-4">
                    <div class="text-center mb-4">
                        <v-icon :color="'red'" size="64" class="recording-icon">
                            mdi-microphone
                        </v-icon>
                    </div>
                    <div class="text-center mb-4">
                        正在錄音...請說話
                    </div>
                    <div class="text-center mb-4" v-if="isProcessing">
                        正在處理語音...
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    </div>
                    <div class="text-center red--text" v-if="recordingError">
                        {{ recordingError }}
                    </div>
                </v-card-text>
                <v-card-actions class="pb-4">
                    <v-spacer></v-spacer>
                    <v-btn
                        color="grey darken-1"
                        text
                        @click="cancelRecording"
                        :disabled="isProcessing"
                    >
                        取消
                    </v-btn>
                    <v-btn
                        color="red darken-1"
                        dark
                        @click="toggleRecording"
                        :loading="isProcessing"
                        :disabled="isProcessing"
                    >
                        停止錄音
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- 新增測驗結果對話框 -->
        <v-dialog v-model="showResultDialog" persistent max-width="1000">
            <v-card>
                <v-card-title class="headline d-flex justify-space-between align-center">
                    測驗結果
                    <v-btn icon @click="handleResultClose">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text>
                    <score-sheet 
                        v-if="examResult"
                        :exam-data="examResult"
                        :readonly="true"
                        @close="handleResultClose"
                    />
                </v-card-text>
            </v-card>
        </v-dialog>
        <v-dialog v-model="showGradingDialog" persistent width="300">
            <v-card>
                <v-card-text class="pa-6">
                    <div class="d-flex justify-center">
                        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
                    </div>
                    <div class="text-h6 mt-4 text-center">評分中...</div>
                    <div class="text-subtitle-1 mt-2 text-center">請稍候</div>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- 新增答題過程查看對話框 -->
            <exam-answer-viewer 
                v-if="selectedExamResult && studentAnswer"
                v-model="showAnswerViewer" 
                :exam-result="selectedExamResult" 
                :student-answer="studentAnswer"
                @close="showAnswerViewer = false"
            />

    </div>
</template>

<script>
import TestService from '../../services/test.service'
import PatientService from '../../services/patient.service'
import baseUrl from '../../../baseUrl.config';
import ChatService from '../../services/chat.service'
import ExamAnswerService from '../../services/examAnswer.service'
import ScoreSheet from '@/components/ScoreSheet.vue'
import ExamResultService from '../../services/examResult.service'
import RecorderService from '@/services/recorder.service'
import AudioService from '@/services/audio.service'
import ExamAnswerViewer from '@/components/ExamAnswerViewer.vue'

export default {
    name: 'StudentExam',
    components: {
        ScoreSheet,
        ExamAnswerViewer
    },
    data() {
        return {
            examStatus: 'upcoming',
            showExamDialog: false,
            showScoreSheet: false,
            currentExam: null,
            currentExamPatient: { name: "", gender: "", age: "", description: "", taskDescription: "", backgroundImage: "", clinicFiles: [], firstVisitFiles: [], postOpFiles: [], firstVisitQuestions: [], postOpQuestions: [], firstVisitAnswers: [], postOpAnswers: [] },
            examContent: {
                patientInfo: {
                    name: '林凱瑜',
                    gender: '男',
                    age: '25',
                    chiefComplaint: '患者是一個25歲男性青年，個性開朗，平時有運動習慣側方臼齒痛，患者是一個25歲男性青年，個性開朗，平時有運動習慣，右側方臼齒痛，患者是一個25歲男性青年，個性開朗，平時有運動習慣，右側方臼齒痛，患者是一個25歲男性青年，個性開朗，平時有運動習慣，右側方臼齒痛，患者是一個25歲男性青年，個性開朗，平時有運動習慣，右側方臼齒痛。',
                    images: [
                        { id: 1, url: 'https://cdn.vuetifyjs.com/images/cards/cooking.png' },
                        { id: 2, url: 'https://cdn.vuetifyjs.com/images/cards/cooking.png' },
                        { id: 3, url: 'https://cdn.vuetifyjs.com/images/cards/cooking.png' },
                        { id: 4, url: 'https://cdn.vuetifyjs.com/images/cards/cooking.png' },
                        { id: 5, url: 'https://cdn.vuetifyjs.com/images/cards/cooking.png' },
                        { id: 6, url: 'https://cdn.vuetifyjs.com/images/cards/cooking.png' },
                        { id: 7, url: 'https://cdn.vuetifyjs.com/images/cards/cooking.png' },
                        { id: 8, url: 'https://cdn.vuetifyjs.com/images/cards/cooking.png' },
                        { id: 9, url: 'https://cdn.vuetifyjs.com/images/cards/cooking.png' },
                        { id: 10, url: 'https://cdn.vuetifyjs.com/images/cards/cooking.png' }
                    ]
                },
            },
            upcomingExamList: [
            ],
            completedExamList: [
            ],
            currentUserId: 'student',
            messageInput: '',
            messages: [],
            currentStep: 1,
            showImageDialog: false,
            selectedImage: '',
            commonSymptoms: ['高血壓', '骨關節發炎', '血液不正', '慢性失眠', '心律不整', '過敏', '暈眩'],
            selectedSymptoms: [],
            examQuestions: [
                { title: '問題一', answer: '' },
                { title: '如果發現病患大量出血要怎麼辦', answer: '' },
                { title: '問題三', answer: '' },
                { title: '問題四', answer: '' },
                { title: '問題五', answer: '' },
                { title: '問題六', answer: '' },
                { title: '問題七', answer: '' },
                { title: '問題八', answer: '' },
                { title: '問題九', answer: '' },
            ],
            currentFirstVisitQuestionIndex: 0,
            currentPostOpQuestionIndex: 0,
            isWaiting: false,

            examData: {
                exam: {
                    name: '',
                    duration: 0
                },
                student: {
                    name: ''
                },
                examResult: {
                    result: 0,
                    comment: ''
                },
                teacher: {
                    name: ''
                },
                examResultDetail: []
            },
            isRecording: false,
            isProcessing: false,
            showRecordingDialog: false,
            recordingError: '',
            currentRecordingTarget: null,
            showResultDialog: false,
            examResult: null,
            showGradingDialog: false,
            showAnswerViewer: false,
            selectedExamResult: {
                name: "",
                gender: "",
                age: "",
                description: "",
                taskDescription: "",
                backgroundImage: "",
                clinicFiles: [],
                firstVisitFiles: [],
                postOpFiles: [],
                firstVisitQuestionExist: true,
                postOpQuestionExist: true,
                student_id: "",
                messages: [],
                firstVisitQuestions: [],
                postOpQuestions: []
            },
            studentAnswer: {
                messages: [],
                answers: {}
            }
        }
    },
    mounted() {
        this.getTestList()
        this.ifExamExist()
        this.getExamResult()
        this.scrollToBottom()
    },
    methods: {
        async viewAnswerProcess(exam) {
            try {
                const response = await ExamResultService.teacherGetExamResultDetail(exam._id);
                
                if (response.data && response.data.data) {
                    // 先設置資料
                    this.selectedExamResult = {
                        name: response.data.data.patient?.name || '',
                        gender: response.data.data.patient?.gender || '',
                        age: response.data.data.patient?.age || '',
                        description: response.data.data.patient?.description || '',
                        taskDescription: response.data.data.patient?.taskDescription || '',
                        backgroundImage: response.data.data.patient?.backgroundImage || '',
                        clinicFiles: response.data.data.patient?.clinicFiles || [],
                        firstVisitFiles: response.data.data.patient?.firstVisitFiles || [],
                        postOpFiles: response.data.data.patient?.postOpFiles || [],
                        firstVisitQuestionExist: response.data.data.patient?.firstVisitQuestionExist || false,
                        postOpQuestionExist: response.data.data.patient?.postOpQuestionExist || false,
                        student_id: response.data.data.patient?.student_id || '',
                        messages: response.data.data.patient?.messages || [],
                        firstVisitQuestions: response.data.data.patient?.firstVisitQuestions || [],
                        postOpQuestions: response.data.data.patient?.postOpQuestions || []
                    };

                    this.studentAnswer = {
                        messages: response.data.data.exam_result_id?.messages || [],
                        answers: response.data.data.exam_result_id?.answerData?.[0] || {}
                    };

                    // 確保資料設置完成後再顯示對話框
                    await this.$nextTick();
                    this.showAnswerViewer = true;
                  
                } else {
                    console.error('Invalid response data structure:', response);
                    throw new Error('無法獲取答題資料');
                }
            } catch (error) {
                console.error('Error fetching exam result details:', error);
                alert('獲取答題過程時發生錯誤，請稍後再試');
            }
        },
        async viewExamScore(exam) {
            try {
                const response = await ExamResultService.teacherGetExamResultDetail(exam._id);
                if (response.data.data) {
                    this.examResult = {
                        exam: {
                            name: exam.exam.name,
                            duration: response.data.data.duration || 0
                        },
                        student: {
                            name: localStorage.getItem('name')
                        },
                        examScore: response.data.data.examScore || 0,
                        examDuration: response.data.data.duration || 0,
                        examResult: {
                            result: response.data.data.result || 0,
                            comment: response.data.data.comment || ''
                        },
                        examResultDetail: response.data.data.examResultDetail || [],
                        teacher: {
                            name: response.data.data.teacher?.name || '考官'
                        }
                    };
                    this.showResultDialog = true;
                }
            } catch (error) {
                console.error('Error fetching exam score:', error);
            }
        },
        handleClose() {
            this.showScoreSheet = false
        },
        handleSubmit(data) {
            console.log(data)
        },
        handleExportCSV() {
            const csvContent = this.examData.examResultDetail.map(section => {
                const sectionTitle = section.SectionTitle;
                const items = section.items.map(item => `${item.title},${item.result},${item.comment}`).join('\n');
                return `${sectionTitle}\n${items}`;
            }).join('\n\n');
            const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.setAttribute('download', 'exam_results.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
        ifExamExist() {
            if (localStorage.getItem('exam')) {
                this.currentExam = JSON.parse(localStorage.getItem('exam'))
                this.currentExamPatient = JSON.parse(localStorage.getItem('currentExamPatient'))
                this.messages = JSON.parse(localStorage.getItem('messageCache'))
                this.showExamDialog = true;
            }
        },
        getTestList() {
            TestService.studentGetTest().then((res) => {
                this.upcomingExamList = res.data.tests
            })
        },
        getExamResult() {
            ExamResultService.studentGetExamResult().then((res) => {
                this.completedExamList = res.data.data
            })
        },
        saveTesttoLocalStorage() {
            localStorage.setItem('exam', JSON.stringify(this.currentExam))
            localStorage.setItem('currentExamPatient', JSON.stringify(this.currentExamPatient))
            localStorage.setItem('messageCache', JSON.stringify(this.messages))
            localStorage.setItem('patient_id', this.currentExamPatient._id)
        },
        generateUUID() {
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
                var r = Math.random()*16|0, v = c == 'x' ? r : (r&0x3|0x8);
                return v.toString(16);
            });
        },
        startExam(exam, patientId) {
            localStorage.removeItem('exam')
            localStorage.removeItem('currentExamPatient')
            localStorage.removeItem('messageCache')
            localStorage.removeItem('patient_id')
            this.messages = []
            PatientService.getPatientByStudent(patientId).then((res) => {
                if (res.data.data) {
                    this.currentExam = exam;
                    this.currentExamPatient = res.data.data;
                    this.currentExamPatient.startTime = new Date();
                    this.currentExamPatient.chat_id=this.generateUUID();
                    if (this.currentExamPatient.firstVisitQuestionsExist === undefined) {
                        this.currentExamPatient.firstVisitQuestionsExist = true
                    }
                    if (this.currentExamPatient.postOpQuestionsExist === undefined) {
                        this.currentExamPatient.postOpQuestionsExist = true
                    }
                    if (this.currentExamPatient.softQuestionsExist === undefined) {
                        this.currentExamPatient.softQuestionsExist = false
                    }
                    this.showExamDialog = true;
                    this.saveTesttoLocalStorage()
                }
            })

        },

        handleSendMessage() {
            if (!this.messageInput.trim()) return;

            const message = {
                _id: Date.now(),
                content: this.messageInput,
                sender_id: this.currentUserId,
                timestamp: new Date().getTime(),
                chat:{_id:this.currentExamPatient.chat_id},
                patient:{_id:this.currentExamPatient._id},
                student:{_id:localStorage.getItem('user_id')}
            };

            this.messages.push(message);
            this.messageInput = '';
            this.isWaiting = true; // 開始顯示等待動畫

            this.saveTesttoLocalStorage()

            ChatService.chat(message).then((res) => {
                const replyMessage = {
                    _id: Date.now(),
                    content: res.data.response,
                    sender_id: 'patient',
                    timestamp: new Date().getTime(),

                };
                console.log(replyMessage)
                this.messages.push(replyMessage);
                this.saveTesttoLocalStorage()
                this.isWaiting = false; // 結束等待動畫
                this.scrollToBottom()
            }).catch(() => {
                const replyMessage = {
                    _id: Date.now(),
                    content: '發生錯誤，請稍後再試',
                    sender_id: 'patient',
                    timestamp: new Date().getTime()
                };
                this.messages.push(replyMessage);
                this.saveTesttoLocalStorage()
                this.isWaiting = false; // 確保錯誤時也會關閉等待動畫
                this.scrollToBottom()
            });
        },
        scrollImages(direction) {
            const container = this.$refs.imageContainer2;
            const scrollAmount = 180 + 16; // 圖片寬度 + gap

            if (direction === 'left') {
                container.scrollLeft -= scrollAmount;
            } else {
                container.scrollLeft += scrollAmount;
            }
        },
        openImageDialog(imageUrl) {
            this.selectedImage = imageUrl;
            this.showImageDialog = true;
        },
        // toggleSymptom(symptom) {
        //     const index = this.selectedSymptoms.indexOf(symptom)
        //     if (index === -1) {
        //         this.selectedSymptoms.push(symptom)
        //     } else {
        //         this.selectedSymptoms.splice(index, 1)
        //     }
        // },
        previousFirstVisitQuestion() {
            if (this.currentFirstVisitQuestionIndex > 0) {
                this.currentFirstVisitQuestionIndex--;
            }
        },
        nextFirstVisitQuestion() {
            if (this.currentFirstVisitQuestionIndex < this.currentExamPatient.firstVisitQuestions.length - 1) {
                this.currentFirstVisitQuestionIndex++;
            }
        },
        previousPostOpQuestion() {
            if (this.currentPostOpQuestionIndex > 0) {
                this.currentPostOpQuestionIndex--;
            }
        },
        nextPostOpQuestion() {
            if (this.currentPostOpQuestionIndex < this.currentExamPatient.postOpQuestions.length - 1) {
                this.currentPostOpQuestionIndex++;
            }
        },
        formatDateTime(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleString('zh-TW', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            }).replace(/\//g, '-');
        },
        previousStep() {
            if (this.currentStep === 3) {
                if (this.currentExamPatient.firstVisitQuestionsExist) {
                    this.currentStep = 2;
                } else {
                    this.currentStep = 1;
                }
            } else if (this.currentStep === 2) {
                this.currentStep = 1;
            }
        },
        nextStep() {
            if (this.currentStep === 1) {
                if (this.currentExamPatient.firstVisitQuestionsExist) {
                    this.currentStep = 2;
                } else if (this.currentExamPatient.postOpQuestionsExist) {
                    this.currentStep = 3;
                }
            } else if (this.currentStep === 2 && this.currentExamPatient.postOpQuestionsExist) {
                this.currentStep = 3;
            }
        },
        submitAnswer() {
            this.currentExamPatient.exam = this.currentExam._id
            this.currentExamPatient.endTime = new Date()
            if (this.currentExamPatient.startTime) {
                this.currentExamPatient.duration = (this.currentExamPatient.endTime - this.currentExamPatient.startTime) / 1000;
            } else {
                this.currentExamPatient.duration = 0;
            }
            this.currentExamPatient.messages = this.messages
            this.showGradingDialog = true
            ExamAnswerService.add(this.currentExamPatient).then((res) => {
                this.showGradingDialog = false

                // 關閉測驗對話框
                this.showExamDialog = false;
                
                // 解析 serverResult 字串
                const serverResult = JSON.parse(res.data.data.serverResult);
                console.log('Server Result:', serverResult);
                
                // 設置測驗結果數據
                this.examResult = {
                    exam: {
                        name: this.currentExam.name,
                        duration: serverResult.examDuration
                    },
                    student: {
                        name: localStorage.getItem('name')
                    },
                    examScore: serverResult.examScore,
                    examDuration: serverResult.examDuration,
                    examResult: {
                        result: serverResult.examResult.result,
                        comment: serverResult.examResult.comment || ''
                    },
                    examResultDetail: serverResult.examResultDetail.map(section => ({
                        SectionTitle: section.SectionTitle,
                        items: Array.isArray(section.items) ? 
                            section.items.map(item => ({
                                title: item.title || '',
                                result: typeof item.result === 'string' ? 1 : item.result,
                                comment: item.comment || ''
                            })) : []
                    })),
                    teacher: {
                        name: '考官'
                    }
                };

                // 顯示結果對話框
                this.showResultDialog = true;
                
                // 清理本地存儲
                this.messages = [];
                localStorage.removeItem('messageCache');
                localStorage.removeItem('exam');
                localStorage.removeItem('patientId');
                localStorage.removeItem('currentExamPatient');
                
                // 重新獲取可測驗列表
                this.getTestList();
            }).catch(error => {
                this.showGradingDialog = false
                console.error('Error submitting answer:', error);
                alert('提交答案時發生錯誤');
            });
        },
        cancelExam() {
            this.currentExam = null;
            this.showExamDialog = false;
            this.messages = []
            localStorage.removeItem('messageCache')
            localStorage.removeItem('exam')
            localStorage.removeItem('patientId')
            localStorage.removeItem('currentExamPatient')
        },
        async handleVoiceInput(targetField) {
            try {
                this.currentRecordingTarget = targetField
                this.showRecordingDialog = true
                this.recordingError = ''
                
                // 立即開始錄音
                this.isRecording = true
                await RecorderService.startRecording()
            } catch (error) {
                console.error('Voice input error:', error)
                this.recordingError = '開始錄音時發生錯誤'
                this.isRecording = false
                this.showRecordingDialog = false
            }
        },
        async toggleRecording() {
            try {
                // 只處理停止錄音
                if (this.isRecording) {
                    this.isRecording = false
                    this.isProcessing = true
                    
                    const audioBlob = await RecorderService.stopRecording()
                    const result = await AudioService.speechToText(audioBlob, this.currentExamPatient._id)
                    
                    // 更新對應的文字欄位
                    if (result && result.text_result) {
                        // 根據目標欄位更新不同的值
                        if (this.currentRecordingTarget === 'messageInput') {
                            this.messageInput = (this.messageInput || '') + (this.messageInput ? ' ' : '') + result.text_result
                        } else if (this.currentRecordingTarget.includes('firstVisitQuestions')) {
                            const currentAnswer = this.currentExamPatient.firstVisitQuestions[this.currentFirstVisitQuestionIndex].answer || ''
                            this.currentExamPatient.firstVisitQuestions[this.currentFirstVisitQuestionIndex].answer = 
                                currentAnswer + (currentAnswer ? ' ' : '') + result.text_result
                        } else if (this.currentRecordingTarget.includes('postOpQuestions')) {
                            const currentAnswer = this.currentExamPatient.postOpQuestions[this.currentPostOpQuestionIndex].answer || ''
                            this.currentExamPatient.postOpQuestions[this.currentPostOpQuestionIndex].answer = 
                                currentAnswer + (currentAnswer ? ' ' : '') + result.text_result
                        }
                        
                        // 儲存更新到 localStorage
                        this.saveTesttoLocalStorage()
                        
                        // 關閉對話框
                        setTimeout(() => {
                            this.isProcessing = false
                            this.showRecordingDialog = false
                            this.currentRecordingTarget = null
                        }, 500) // 短暫延遲以顯示處理完成
                    }
                }
            } catch (error) {
                console.error('Voice input error:', error)
                this.recordingError = '語音輸入發生錯誤'
                this.isRecording = false
                this.isProcessing = false
            }
        },
        cancelRecording() {
            if (this.isRecording) {
                RecorderService.stopRecording()
                this.isRecording = false
            }
            this.isProcessing = false
            this.showRecordingDialog = false
            this.currentRecordingTarget = null
            this.recordingError = ''
        },
        returnImageUrl(imageUrl) {
            return baseUrl + "uploadFiles/backgroundImage/" + imageUrl
        },
        returnClinicFileUrl(imageUrl) {
            return baseUrl + "uploadFiles/clinicFiles/" + imageUrl
        },
        returnFirstVisitFileUrl(imageUrl) {
            return baseUrl + "uploadFiles/firstVisitFiles/" + imageUrl
        },
        returnPostOpFileUrl(imageUrl) {
            return baseUrl + "uploadFiles/postOpFiles/" + imageUrl
        },
        handleResultClose() {
            this.showResultDialog = false;
            this.examResult = null;
        },
        scrollToBottom() {
            const container = this.$refs.messagesContainer;
            container.scrollTop = container.scrollHeight;
        },
    }
}
</script>

<style scoped>
.exam-container {
    width: 100%;
    height: 100%;
    padding: 0;
    margin: 0;
    background-color: white;
    overflow: hidden;
}

.v-btn-toggle {
    background-color: #f5f5f5;
    margin: 20px;
}

.v-btn-toggle .v-btn {
    background-color: transparent !important;
    color: #666 !important;
}

.v-btn-toggle .v-btn.v-item--active {
    background-color: #000 !important;
    color: white !important;
}

/* 表格樣式 */
.exam-table {
    width: 100%;
    border-radius: 0;
}

.v-data-table {
    width: 100%;
}

.v-data-table th {
    background-color: #fff !important;
    color: #333 !important;
    font-weight: bold !important;
    text-transform: none !important;
    padding: 10px 25px !important;
}

.v-data-table td {
    border-bottom: 1px solid #eee;
    padding: 20px 25px !important;
}

/* 確保表格頭部的背景色伸到整行 */
thead tr {
    background-color: #f5f5f5;
}

/* 調整格行高 */
tbody tr {
    height: 60px;
}

/* 新增話框樣式 */
.exam-dialog {
    display: flex;
    flex-direction: column;
    height: 100vh;
    width: 100vw;
    background-color: #f8f8f8;
    overflow: hidden;
}

.progress-container {
    display: flex;
    align-items: center;
    padding: 16px 32px;
    background-color: white;
    border-bottom: 1px solid #eee;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.content-container {
    display: flex;
    height: calc(100vh - 65px);
    gap: 32px;
    padding: 32px;
    background-color: #f8f8f8;
}

.patient-section {
    flex: 1;
    min-width: 500px;
    max-width: 40%; 
    display: flex;
    flex-direction: column;
    gap: 24px;
    background: white;
    padding: 24px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.video-section {
    width: 100%;
    height: 800px;
    border-radius: 12px;
    overflow: hidden;
    background: #000;
}

.video-placeholder {
    width: 100%;
    height: 100%;
}

.patient-info {
    height: 100%;
    background: #f8f8f8;
    padding: 8px;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
}

.info-group {
    margin: 0 12px;
}

.info-label {
    font-weight: 600;
    color: #666;
    margin-bottom: 8px;
}

.chief-complaint {
    margin-top: 8px;
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.chief-complaint .v-textarea {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.chief-complaint .v-textarea >>> .v-input__slot {
    flex: 1;
    min-height: 0 !important;
}

.chief-complaint .v-textarea >>> textarea {
    height: 100% !important;
    overflow-y: auto;
}

.exam-form-section {
    flex: 2;
    min-width: 600px;
    max-width: 60%;
    display: flex;
    flex-direction: column;
    gap: 24px;
    background: white;
    padding: 24px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.image-section {
    width: 100%;
    height: 100%;
    padding: 20px;
    background: #f8f8f8;
    border-radius: 12px;
    margin-bottom: 24px;
}

.image-slider-container {
    position: relative;
    width: 100%;
    padding: 0 48px;
    margin-top: 16px;
}

.image-scroll-container {
    height: 100%;
    display: flex;
    gap: 20px;
    overflow-x: auto;
    padding: 12px 0;
    scroll-behavior: smooth;
    -ms-overflow-style: none;
    scrollbar-width: none;
}

.image-wrapper {
    flex: 0 0 auto;
    width: 320px;
    height: 240px;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.chat-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 20px;
    min-height: 200px;
    max-height: 400px;
    background: #f8f8f8;
    border-radius: 12px;
    padding: 20px;
    overflow: hidden;
}

.messages-container {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    max-height: 310px;
    background: white;
    border-radius: 12px;
    box-shadow: inset 0 2px 4px rgba(0,0,0,0.05);
}

.message {
    margin-bottom: 16px;
    padding: 12px 16px;
    border-radius: 12px;
    max-width: 30%;
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
    font-size: 20px;
}

.message-sent {
    background: #000;
    color: white;
    margin-left: auto;
}

.message-received {
    background: #f0f0f0;
    margin-right: auto;
}

.input-container {
    display: flex;
    gap: 16px;
    padding: 20px;
    background: white;
    border-radius: 12px;
    box-shadow: 0 -2px 4px rgba(0,0,0,0.05);
}

.v-text-field.dense >>> .v-input__slot {
    min-height: 48px !important;
    background: #f8f8f8 !important;
}

.action-buttons {
    display: flex;
    gap: 12px;
}

.action-button {
    min-width: 48px !important;
    width: 48px !important;
    height: 48px !important;
}

.slider-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
    background: rgba(0, 0, 0, 0.7) !important;
    width: 36px !important;
    height: 36px !important;
    border-radius: 50% !important;
}

.slider-arrow.left {
    left: 4px;
}

.slider-arrow.right {
    right: 4px;
}

.progress-btn.active {
    background-color: #000 !important;
    color: white !important;
}

.progress-btn {
    min-width: 80px;
}

.questions-list {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.question-navigation {
    display: flex;
    align-items: flex-start;
    border-bottom: 1px solid #e0e0e0;
    padding-bottom: 8px;
}

.question-nav-items {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
}

.nav-item {
    padding: 8px 16px;
    cursor: pointer;
    color: #666;
    font-size: 14px;
}

.nav-item.active {
    color: #000;
    font-weight: 500;
}

.nav-arrows {
    display: flex;
    gap: 8px;
}

.question-content {
    padding: 16px 0;
}

.question-title {
    font-weight: bold;
    margin-bottom: 8px;
}

.question-text {
    margin-bottom: 24px;
}

.answer-label {
    font-weight: bold;
    margin-bottom: 8px;
}

.v-chip {
    height: 32px !important;
    font-size: 14px !important;
}

.v-chip.v-chip--outlined {
    border: 1px solid #e0e0e0 !important;
}

.questions-answer-container {
    display: grid;
    grid-template-columns: 200px 1fr;
    gap: 24px;
    border-top: 1px solid #e0e0e0;
    padding-top: 24px;
}

.questions-list {
    border-right: 1px solid #e0e0e0;
    padding-right: 16px;
}

.nav-item {
    padding: 12px 16px;
    cursor: pointer;
    color: #666;
    font-size: 14px;
    margin-bottom: 8px;
}

.nav-item.active {
    color: #000;
    font-weight: 500;
}

.nav-arrows {
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-top: 16px;
}

.answer-container {
    padding-left: 16px;
}

.question-title {
    font-weight: bold;
    font-size: 16px;
}

.question-text {
    font-size: 16px;
    color: #333;
}

.answer-label {
    font-weight: bold;
    font-size: 16px;
}

.v-chip {
    height: 32px !important;
    font-size: 14px !important;
}

.v-chip.v-chip--outlined {
    border: 1px solid #e0e0e0 !important;
}

.large-chip {
    height: 40px !important;
    font-size: 16px !important;
    padding: 0 20px !important;
}

.large-chip .v-chip__content {
    font-weight: 500;
}

/* 添加打字動畫樣式 */
.typing-indicator {
    background-color: #f5f5f5 !important;
    padding: 12px 20px !important;
    display: flex;
    align-items: center;
    gap: 4px;
}

.typing-indicator span {
    height: 8px;
    width: 8px;
    background: #666;
    border-radius: 50%;
    display: inline-block;
    animation: bounce 1.3s linear infinite;
}

.typing-indicator span:nth-child(2) {
    animation-delay: 0.15s;
}

.typing-indicator span:nth-child(3) {
    animation-delay: 0.3s;
}

@keyframes bounce {

    0%,
    60%,
    100% {
        transform: translateY(0);
    }

    30% {
        transform: translateY(-4px);
    }
}

.mic-button {
    margin-left: 8px;
    cursor: pointer;
}

.mic-button.recording {
    color: #ff4d4f;
}

.mic-button.processing {
    color: #1890ff;
}

.v-dialog .v-card {
    display: flex;
    flex-direction: column;
}

.v-dialog .v-card__text {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.recording-icon {
    animation: pulse 1.5s infinite;
}

@keyframes pulse {
    0% {
        transform: scale(1);
        opacity: 1;
    }
    50% {
        transform: scale(1.2);
        opacity: 0.8;
    }
    100% {
        transform: scale(1);
        opacity: 1;
    }
}
</style>
